import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  LogOut, AlertCircle, CheckCircle, MapPin, Shield, FileText, 
  DollarSign, Users, Download, XCircle, Clock, Eye, BarChart3, Lock, Plug
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
import Lock24hBanner from '../../components/Lock24hBanner';
import AdminBackendMisconfigAlert from '../../components/AdminBackendMisconfigAlert';
import DataIntegrityCard from '../../components/DataIntegrityCard';
import DiversitySnapshotCard from '../../components/DiversitySnapshotCard';  // Iter-282c
import PlatformBalanceBanner from '../../components/PlatformBalanceBanner';   // P0.16 follow-up
import SwVersionDistributionCard from '../../components/SwVersionDistributionCard';
import SmsPipelineHealthCard from '../../components/SmsPipelineHealthCard';
import PendingApprovalsCard from '../../components/PendingApprovalsCard';
import OnboardingFunnelCard from '../../components/OnboardingFunnelCard';
import EnablePushBanner from '../../components/EnablePushBanner';
import useIntegrationsHealth from '../../hooks/useIntegrationsHealth';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [idvStats, setIdvStats] = useState(null);
  const [idvList, setIdvList] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [pendingCheckins, setPendingCheckins] = useState([]);
  const [pendingPremium, setPendingPremium] = useState([]);
  const [newContract, setNewContract] = useState({ title: '', description: '', pdf_url: '', audience: 'talent', version: '1.0', is_required: true });
  const [showContractDialog, setShowContractDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [adminPermissions, setAdminPermissions] = useState(null);
  const [adminRole, setAdminRole] = useState(null);
  // Iter-213 P0 — Pending counts surface inline badges on quick action
  // tiles so admins can spot work at a glance without opening each queue.
  const [pendingCounts, setPendingCounts] = useState({
    client: 0, ai_creator: 0, idv: 0, compliance: 0,
  });
  // Iter-220g — integration health badge for the Integrations Quick
  // Action tile + inline card. Surfaces Xero re-auth + Yardstik state
  // without admins having to open the page.
  const integrationsHealth = useIntegrationsHealth();

  useEffect(() => {
    fetchData();
    fetchAdminPermissions();
    fetchPendingCounts();
  }, []);

  const fetchPendingCounts = async () => {
    const probes = [
      ['client',     '/admin/client-approvals?status=pending',  (d) => d?.counts?.pending ?? (d?.clients || []).length],
      ['ai_creator', '/admin/ai-creators?status=pending',       (d) => (Array.isArray(d) ? d.length : (d?.length ?? 0))],
      ['idv',        '/admin/dashboard/stats',                  (d) => d?.idv_pending ?? 0],
      ['compliance', '/attendance/clock-in-reviews/count',      (d) => Object.values(d?.breakdown || {}).reduce((a,b)=>a+(b||0),0)],
    ];
    const out = { client: 0, ai_creator: 0, idv: 0, compliance: 0 };
    await Promise.all(probes.map(async ([key, path, pick]) => {
      try {
        const r = await api.get(path);
        out[key] = Number(pick(r.data)) || 0;
      } catch { /* tolerate */ }
    }));
    setPendingCounts(out);
  };

  const fetchAdminPermissions = async () => {
    try {
      const res = await api.get('/admin/permissions');
      setAdminPermissions(res.data.permissions);
      setAdminRole(res.data.admin_role);
    } catch (e) {
      console.error('Failed to load admin permissions:', e);
    }
  };

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      // Fetch IDV stats
      const idvStatsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/idv/admin/stats`, { headers });
      const idvStatsData = await idvStatsRes.json();
      setIdvStats(idvStatsData);

      // Fetch IDV list (needs review)
      const idvListRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/idv/admin/overview?status=needs_review`, { headers });
      const idvListData = await idvListRes.json();
      setIdvList(idvListData);

      // Fetch contracts
      const contractsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contracts/admin/list`, { headers });
      const contractsData = await contractsRes.json();
      setContracts(contractsData);

    } catch (error) {
      console.error('Error loading admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateContract = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contracts/admin/create`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify(newContract)
      });
      if (res.ok) {
        toast.success('Contract created successfully');
        setShowContractDialog(false);
        setNewContract({ title: '', description: '', pdf_url: '', audience: 'talent', version: '1.0', is_required: true });
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to create contract');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#03DFFC]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <button onClick={() => navigate('/admin/home')} className="text-2xl font-bold text-[#03DFFC] hover:opacity-80 transition-opacity" data-testid="admin-home-link">iEvntür Admin</button>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#03DFFC]">{user?.email}</span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleLogout} 
              className="border-gray-200 text-gray-600 hover:bg-gray-50"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Iter-192.2 — Lock in next 24h sticky banner */}
      <Lock24hBanner />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Iter-221 — Admin-only backend misconfig alert (replaces public banner). */}
        <AdminBackendMisconfigAlert />
        {/* Iter-213 P0 — Pending Approvals (Discoverability surface).
            Mounted at the very top so admins immediately see what
            actions need them: client requests, AI creators, IDV, compliance. */}
        <PendingApprovalsCard />

        {/* Iter-216 — Onboarding Funnel tile. Surfaces talent IDV
            pipeline (Not Started, Pending Review, Pending >24h/48h,
            Approved, Rejected) with bulk + per-talent reminders. */}
        <OnboardingFunnelCard />

        {/* Admin Quick Actions */}
        <div className="mb-8" data-testid="admin-quick-actions">
          {/* Iter-245 — Push enrollment banner for admins. Hides
              automatically once the user enables push or dismisses. */}
          <EnablePushBanner />
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Quick Actions</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
            {[
              { label: 'Users', icon: '👥', path: '/admin/users' },
              { label: 'Invoices', icon: '🧾', path: '/admin/invoices' },
              { label: 'Impersonate', icon: '🔄', path: '/admin/users' },
              { label: 'CSV Import', icon: '📥', path: '/admin/csv-import' },
              { label: 'Analytics', icon: '📊', path: '/admin/analytics' },
              { label: 'Referrals', icon: '🏆', path: '/admin/referral-leaderboard' },
              { label: 'Certifications', icon: '📋', path: '/admin/certifications' },
              { label: 'Client Approvals', icon: '🏢', path: '/admin/client-approvals', badge: pendingCounts.client },
              { label: 'Requirement Approvals', icon: '✨', path: '/admin/requirement-approvals' },
              { label: 'Cert Audit Log', icon: '🛡️', path: '/admin/compliance/cert-access-log' },
              { label: 'Shifts', icon: '📅', path: '/admin/shifts' },
              { label: 'Integrations', icon: '🔌', path: '/admin/integrations',
                badge: integrationsHealth.alertCount,
                badgeTone: integrationsHealth.worstSeverity === 'warn' ? 'amber' : null,
                badgeLabel: integrationsHealth.xero.needsReauth ? 'Re-auth' : null,
              },
            ].map(action => (
              <button
                key={action.label}
                onClick={() => navigate(action.path)}
                className="relative flex flex-col items-center gap-1.5 p-3 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 hover:border-cyan-300 transition-all text-center"
                data-testid={`admin-qa-${action.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                {action.badge > 0 && (
                  <span
                    className={`absolute -top-1.5 -right-1.5 ${
                      action.badgeLabel ? 'min-w-[58px] px-2' : 'min-w-[20px] px-1.5'
                    } h-5 rounded-full ${
                      action.badgeTone === 'amber'
                        ? 'bg-amber-500 text-white'
                        : 'bg-rose-500 text-white'
                    } text-[11px] font-semibold flex items-center justify-center shadow-sm`}
                    data-testid={`admin-qa-${action.label.toLowerCase().replace(/\s/g, '-')}-badge`}
                  >
                    {action.badgeLabel ? action.badgeLabel : (action.badge > 99 ? '99+' : action.badge)}
                  </span>
                )}
                <span className="text-xl">{action.icon}</span>
                <span className="text-xs font-medium text-gray-700">{action.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <Card className="bg-white border-gray-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Total Talent</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">{idvStats?.total_talent || 0}</div>
            </CardContent>
          </Card>

          <Card className="bg-white border-gray-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">IDV Approved</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{idvStats?.approved || 0}</div>
            </CardContent>
          </Card>

          <Card className="bg-white border-gray-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">IDV Pending</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-500">{idvStats?.pending || 0}</div>
            </CardContent>
          </Card>

          <Card className="bg-white border-gray-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Needs Review</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-500">{idvStats?.needs_review || 0}</div>
            </CardContent>
          </Card>

          <Card className="bg-white border-gray-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Verification Rate</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-[#03DFFC]">{idvStats?.verification_rate || 0}%</div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Links to Admin Pages - Permission Aware */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {/* Talent Approval - Talent Admin or Super Admin */}
          {(adminPermissions?.can_manage_talent) && (
            <Button
              onClick={() => navigate('/admin/talent-approval')}
              variant="outline"
              className="h-16 border-gray-200 text-gray-600 hover:bg-gray-50 justify-start px-4"
              data-testid="talent-approval-link"
            >
              <Shield className="w-5 h-5 mr-3 text-[#03DFFC]" />
              <div className="text-left">
                <p className="font-medium">Talent Approval</p>
                <p className="text-xs text-gray-400">Review and manage talent applications</p>
              </div>
            </Button>
          )}
          
          {/* Contract Templates - Client Admin or Super Admin */}
          {(adminPermissions?.can_manage_contracts || adminPermissions?.can_manage_admins) && (
            <Button
              onClick={() => navigate('/admin/contracts')}
              variant="outline"
              className="h-16 border-gray-200 text-gray-600 hover:bg-gray-50 justify-start px-4"
              data-testid="contract-manager-link"
            >
              <FileText className="w-5 h-5 mr-3 text-[#03DFFC]" />
              <div className="text-left">
                <p className="font-medium">Contract Templates</p>
                <p className="text-xs text-gray-400">Upload and edit contract language</p>
              </div>
            </Button>
          )}
          
          {/* IDV Dashboard surfaced removed per Iter-246b (Mel) — admins
              who still need to action IDV submissions navigate to
              /admin/idv-review directly. */}
          
          {/* Rate Configuration - Finance Admin or Super Admin */}
          {(adminPermissions?.can_view_financial) && (
            <Button
              onClick={() => navigate('/admin/rate-config')}
              variant="outline"
              className="h-16 border-gray-200 text-gray-600 hover:bg-gray-50 justify-start px-4"
              data-testid="rate-config-link"
            >
              <DollarSign className="w-5 h-5 mr-3 text-[#03DFFC]" />
              <div className="text-left">
                <p className="font-medium">Rate Configuration</p>
                <p className="text-xs text-gray-400">Manage bill rates and payouts per role</p>
              </div>
            </Button>
          )}
          
          {/* Analytics - Finance Admin or Super Admin */}
          {(adminPermissions?.can_view_financial) && (
            <Button
              onClick={() => navigate('/admin/analytics')}
              variant="outline"
              className="h-16 border-gray-200 text-gray-600 hover:bg-gray-50 justify-start px-4"
              data-testid="analytics-dashboard-link"
            >
              <BarChart3 className="w-5 h-5 mr-3 text-emerald-400" />
              <div className="text-left">
                <p className="font-medium">Monetization Analytics</p>
                <p className="text-xs text-gray-400">Track revenue and performance metrics</p>
              </div>
            </Button>
          )}
          
          {/* Bookings - Client Admin or Super Admin */}
          {(adminPermissions?.can_manage_bookings || adminPermissions?.can_manage_clients) && (
            <Button
              onClick={() => navigate('/admin/operations')}
              variant="outline"
              className="h-16 border-gray-200 text-gray-600 hover:bg-gray-50 justify-start px-4"
              data-testid="operations-link"
            >
              <Users className="w-5 h-5 mr-3 text-amber-500" />
              <div className="text-left">
                <p className="font-medium">Operations Center</p>
                <p className="text-xs text-gray-400">Manage bookings and schedules</p>
              </div>
            </Button>
          )}

          {/* Integrations — Xero accounting + Yardstik background checks.
              Iter-220g — gate removed. Per product call this tile must be
              visible to every admin (Xero/Yardstik visibility doesn't
              leak sensitive data; it just exposes which integrations
              exist + their connection state). Status badge surfaces
              re-auth needs without making the admin open the page. */}
          <Button
            onClick={() => navigate('/admin/integrations')}
            variant="outline"
            className="h-16 border-gray-200 text-gray-600 hover:bg-gray-50 justify-start px-4 relative"
            data-testid="integrations-link"
          >
            <Plug className="w-5 h-5 mr-3 text-violet-500" />
            <div className="text-left flex-1">
              <p className="font-medium">Integrations</p>
              <p className="text-xs text-gray-400">Xero accounting · Yardstik background checks</p>
            </div>
            {integrationsHealth.xero.needsReauth && (
              <span
                className="ml-2 inline-flex items-center rounded-full bg-amber-100 text-amber-800 text-[11px] px-2 py-0.5 font-medium border border-amber-200"
                data-testid="integrations-link-xero-badge"
              >
                Xero · Re-auth
              </span>
            )}
            {integrationsHealth.yardstik.mockMode && (
              <span
                className="ml-2 inline-flex items-center rounded-full bg-slate-100 text-slate-700 text-[11px] px-2 py-0.5 font-medium border border-slate-200"
                data-testid="integrations-link-yardstik-badge"
              >
                Yardstik · Sandbox
              </span>
            )}
            {!integrationsHealth.yardstik.enabled && !integrationsHealth.loading && (
              <span
                className="ml-2 inline-flex items-center rounded-full bg-rose-100 text-rose-800 text-[11px] px-2 py-0.5 font-medium border border-rose-200"
                data-testid="integrations-link-yardstik-off-badge"
              >
                Yardstik · Off
              </span>
            )}
          </Button>
        </div>
        
        {/* Admin Role Badge */}
        <div className="mb-4 flex items-center gap-2">
          <Badge className="bg-[#03DFFC]/10 text-[#03DFFC] border border-[#03DFFC]/20">
            {adminRole === 'super_admin' && 'Super Admin'}
            {adminRole === 'finance_admin' && 'Finance Admin'}
            {adminRole === 'client_admin' && 'Client Admin'}
            {adminRole === 'talent_admin' && 'Talent Admin'}
            {!adminRole && 'Admin'}
          </Badge>
          {adminPermissions?.can_view_sensitive && (
            <Badge className="bg-red-100 text-red-700 border border-red-200">
              Sensitive Data Access
            </Badge>
          )}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-gray-200">
            <TabsTrigger value="overview" className="data-[state=active]:bg-cyan-900/50 data-[state=active]:text-[#03DFFC]">
              <Users className="w-4 h-4 mr-2" />
              IDV Review
            </TabsTrigger>
            <TabsTrigger value="contracts" className="data-[state=active]:bg-cyan-900/50 data-[state=active]:text-[#03DFFC]">
              <FileText className="w-4 h-4 mr-2" />
              Contracts
            </TabsTrigger>
            <TabsTrigger value="payouts" className="data-[state=active]:bg-cyan-900/50 data-[state=active]:text-[#03DFFC]">
              <DollarSign className="w-4 h-4 mr-2" />
              Payouts
            </TabsTrigger>
          </TabsList>

          {/* IDV Review Tab */}
          <TabsContent value="overview">
            {/* P0.16 follow-up — surface Stripe platform balance up front so
                admins never mystery-debug a $0 balance again (Anita Currie regression) */}
            <div className="mb-4">
              <PlatformBalanceBanner data-testid="dashboard-balance-banner" />
            </div>

            {/* Iter-194 — Production Data Integrity card (seed/test leak guard) */}
            <div className="mb-6">
              <DataIntegrityCard />
            </div>

            {/* Iter-282c — Diversity Snapshot (admin-only ethnicity reporting) */}
            <div className="mb-6">
              <DiversitySnapshotCard />
            </div>

            {/* Iter-198 — Service-Worker version distribution (ops-only) */}
            <div className="mb-6">
              <SwVersionDistributionCard />
            </div>

            {/* Iter-200 — SMS Pipeline Health (Twilio auth + A2P 10DLC carrier-block detection) */}
            <div className="mb-6">
              <SmsPipelineHealthCard />
            </div>

            <Card className="bg-white border-gray-200">
              <CardHeader>
                <CardTitle className="text-gray-900 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-[#03DFFC]" />
                  Identity Verification Queue
                </CardTitle>
              </CardHeader>
              <CardContent>
                {idvList.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-600" />
                    <p>No pending IDV reviews</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {idvList.map((item) => (
                      <div key={item.user_id} className="bg-gray-50 rounded-lg p-4" data-testid="idv-review-item">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-medium text-gray-900">{item.first_name} {item.last_name}</h3>
                            <p className="text-sm text-gray-500">{item.email}</p>
                            <div className="mt-2 flex items-center gap-2">
                              <Badge className="bg-amber-50 text-amber-700 border border-amber-200">
                                <Clock className="w-3 h-3 mr-1" />
                                {item.idv_status}
                              </Badge>
                              {item.idv_reason && (
                                <span className="text-sm text-red-500">{item.idv_reason}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => navigate(`/admin/idv-review`)}
                              className="bg-cyan-600 hover:bg-cyan-700 text-white"
                              data-testid={`idv-review-deeplink-${item.user_id}`}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Review
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contracts Tab */}
          <TabsContent value="contracts">
            <Card className="bg-white border-gray-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-gray-900 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#03DFFC]" />
                  Contract Management
                </CardTitle>
                <Button 
                  onClick={() => setShowContractDialog(true)}
                  className="bg-[#03DFFC] hover:bg-[#03DFFC] text-black"
                >
                  Create Contract
                </Button>
              </CardHeader>
              <CardContent>
                {contracts.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No contracts created yet</p>
                ) : (
                  <div className="space-y-4">
                    {contracts.map((contract) => (
                      <div key={contract.id} className="bg-gray-50 rounded-lg p-4" data-testid="contract-admin-item">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-medium text-gray-900">{contract.title}</h3>
                            <p className="text-sm text-gray-500">{contract.description}</p>
                            <div className="mt-2 flex items-center gap-2">
                              <Badge className={`${
                                contract.audience === 'talent' ? 'bg-purple-900/50 text-purple-300 border-purple-700' :
                                contract.audience === 'client' ? 'bg-blue-900/50 text-blue-300 border-blue-700' :
                                'bg-cyan-900/50 text-[#03DFFC] border-[#03DFFC]/30'
                              }`}>
                                {contract.audience}
                              </Badge>
                              <Badge className="bg-slate-700 text-gray-600">
                                v{contract.version}
                              </Badge>
                              {contract.is_required && (
                                <Badge className="bg-red-900/50 text-red-300 border-red-700">
                                  Required
                                </Badge>
                              )}
                              {!contract.is_active && (
                                <Badge className="bg-slate-700 text-gray-500">
                                  Inactive
                                </Badge>
                              )}
                            </div>
                          </div>
                          <a 
                            href={contract.pdf_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-[#03DFFC] hover:text-[#03DFFC]"
                          >
                            <Eye className="w-5 h-5" />
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payouts Tab - Finance Admin or Super Admin only */}
          <TabsContent value="payouts">
            <Card className="bg-white border-gray-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-gray-900 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-[#03DFFC]" />
                  Payout Overview
                </CardTitle>
                {adminPermissions?.can_view_financial && (
                  <Button 
                    variant="outline"
                    className="border-gray-200 text-gray-600 hover:bg-gray-50"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {adminPermissions?.can_view_financial ? (
                  <div className="text-center text-gray-500 py-8">
                    <DollarSign className="w-12 h-12 mx-auto mb-3 text-[#03DFFC]" />
                    <p>Payout management coming soon</p>
                    <p className="text-sm mt-2">View OA deductions, net payouts, and generate reports</p>
                  </div>
                ) : (
                  <div className="text-center text-gray-500 py-8">
                    <Lock className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p className="font-medium text-gray-700">Access Restricted</p>
                    <p className="text-sm mt-2">Financial data is only visible to Finance Admin and Super Admin</p>
                    <p className="text-xs mt-1 text-gray-400">Contact your administrator for access</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Contract Dialog */}
      <Dialog open={showContractDialog} onOpenChange={setShowContractDialog}>
        <DialogContent className="bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Create New Contract</DialogTitle>
          <DialogDescription className="text-xs text-gray-500">
            Start from a template or upload a signed PDF — the client signs online.
          </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-600">Title *</Label>
              <Input
                value={newContract.title}
                onChange={(e) => setNewContract(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Independent Contractor Agreement"
                className="bg-gray-50 border-gray-200 text-gray-900"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-600">Description</Label>
              <Textarea
                value={newContract.description}
                onChange={(e) => setNewContract(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Terms and conditions for..."
                className="bg-gray-50 border-gray-200 text-gray-900"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-600">PDF URL *</Label>
              <Input
                value={newContract.pdf_url}
                onChange={(e) => setNewContract(prev => ({ ...prev, pdf_url: e.target.value }))}
                placeholder="https://..."
                className="bg-gray-50 border-gray-200 text-gray-900"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-600">Audience</Label>
                <select
                  value={newContract.audience}
                  onChange={(e) => setNewContract(prev => ({ ...prev, audience: e.target.value }))}
                  className="w-full bg-gray-50 border border-gray-200 text-gray-900 rounded-md p-2"
                >
                  <option value="talent">Talent</option>
                  <option value="client">Client</option>
                  <option value="both">Both</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-600">Version</Label>
                <Input
                  value={newContract.version}
                  onChange={(e) => setNewContract(prev => ({ ...prev, version: e.target.value }))}
                  placeholder="1.0"
                  className="bg-gray-50 border-gray-200 text-gray-900"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_required"
                checked={newContract.is_required}
                onChange={(e) => setNewContract(prev => ({ ...prev, is_required: e.target.checked }))}
                className="w-4 h-4"
              />
              <Label htmlFor="is_required" className="text-gray-600">Required contract</Label>
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowContractDialog(false)}
              className="border-gray-200 text-gray-600"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateContract}
              className="bg-[#03DFFC] hover:bg-[#03DFFC] text-black"
              disabled={!newContract.title || !newContract.pdf_url}
            >
              Create Contract
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
